import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Circle, Sparkles, Eye, Lock } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Public, read-only sample of a completed Boardroom session. Linked from the
 * "See how it works" section so first-time visitors can preview the output
 * before booking their own first session.
 *
 * All content is hardcoded — no backend calls, no side effects.
 */

const SAMPLE = {
  title: "Q1 personal review — week 12",
  host_name: "The Chair",
  cadence: "weekly",
  type: "solo",
  when: "Sunday morning · 47 minutes",
  wins: [
    "Shipped Boardroom v2 to production — push notifications live on iOS + Android",
    "Signed the first Founding Member (#001 — 30% referral converted a warm intro)",
    "5 consecutive Sunday sessions logged. The ritual is starting to feel non-negotiable",
  ],
  losses: [
    "Skipped last month's monthly review — let two weeks blur into one",
    "Missed Q1 revenue target by 12%. Underestimated onboarding friction",
  ],
  goals: [
    { label: "Ship weekly to production", progress: 90, status: "on_track" },
    { label: "10 paying users by end of Q1", progress: 40, status: "slipping" },
    { label: "One case study written & published", progress: 0, status: "stalled" },
  ],
  challenges: [
    "Trust in the ritual — some weeks the check-in feels forced. Need to normalise the discomfort.",
    "Deciding what belongs in the weekly vs. what needs a monthly deep-dive.",
  ],
  action_items: [
    { text: "Book Q2 planning session for Sunday 10am", signed: true, due: "Wed" },
    { text: "Cut one recurring commitment before month-end", signed: true, due: "Fri" },
    { text: "Reach out to 3 potential founder buyers", signed: true, due: "Next session" },
    { text: "Post first case study to Substack", signed: false, due: "Sunday" },
  ],
  minutes_summary:
    "Momentum on shipping and community remains strong; the founding-member motion validated the referral engine. Two weak spots emerged: cadence discipline (a skipped monthly) and revenue conversion. Committed to four resolutions before next session, three signed. Goals rebalanced — one on track, one requiring an intervention, one stalled and up for cancellation next week.",
};

function Section({ eyebrow, title, items, empty }) {
  if (!items || items.length === 0) {
    return (
      <section>
        <p className="label-eyebrow">{eyebrow}</p>
        <h3 className="font-serif text-2xl mt-2">{title}</h3>
        <p className="text-sm text-[#666] mt-3">{empty}</p>
      </section>
    );
  }
  return (
    <section>
      <p className="label-eyebrow">{eyebrow}</p>
      <h3 className="font-serif text-2xl mt-2">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((s, i) => (
          <li key={`${i}:${s}`} className="flex gap-3 text-[#ddd]">
            <span className="font-mono text-[#c89f45] text-sm shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function SampleMinutes() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa]" data-testid="sample-minutes-page">
      {/* Sample banner — sticky at the top */}
      <div
        className="sticky top-0 z-40 bg-[#c89f45] text-[#0a0a0a] px-4 py-2 flex items-center justify-between gap-3"
        style={{
          paddingTop: "calc(0.5rem + env(safe-area-inset-top))",
          paddingLeft: "calc(1rem + env(safe-area-inset-left))",
          paddingRight: "calc(1rem + env(safe-area-inset-right))",
        }}
      >
        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <Eye className="w-4 h-4 shrink-0" strokeWidth={2.5} />
          <span className="font-medium">SAMPLE · this is what a completed Boardroom session looks like.</span>
        </div>
        <Link to="/meetings/new" data-testid="sample-cta-top">
          <Button className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-[#c89f45] rounded-md h-8 px-3 text-xs font-medium">
            Call yours
            <ArrowUpRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
        {/* Header row */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#aaa] hover:text-[#fafafa] transition-colors" data-testid="sample-back">
            <ArrowLeft className="w-4 h-4" />
            Back to Boardroom
          </Link>
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[2px] uppercase text-[#666] font-medium">
            <Lock className="w-3 h-3" /> read-only demo
          </span>
        </div>

        <p className="label-eyebrow" data-testid="sample-eyebrow">
          Weekly · Solo · Completed
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#fafafa] mt-2 leading-tight">
          {SAMPLE.title}
        </h1>
        <p className="mt-3 text-sm text-[#888]">
          {SAMPLE.host_name} · {SAMPLE.when}
        </p>

        <div className="gold-divider" />

        {/* AI-generated summary */}
        <section className="my-8 bg-[#121212] border border-[#1a1a1a] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#c89f45]" strokeWidth={1.5} />
            <p className="label-eyebrow">AI-drafted summary</p>
          </div>
          <p className="text-[#ddd] leading-relaxed text-[15px]">{SAMPLE.minutes_summary}</p>
        </section>

        <div className="space-y-10 mt-10">
          <Section eyebrow="Wins" title="What worked this week" items={SAMPLE.wins} empty="—" />
          <Section eyebrow="Losses" title="What didn't" items={SAMPLE.losses} empty="—" />

          {/* Goals review — richer than a bare list */}
          <section>
            <p className="label-eyebrow">Goals review</p>
            <h3 className="font-serif text-2xl mt-2">Where the quarter stands</h3>
            <ul className="mt-4 space-y-3">
              {SAMPLE.goals.map((g, i) => {
                const color =
                  g.status === "on_track" ? "#c89f45" : g.status === "slipping" ? "#e0a04a" : "#666";
                const label =
                  g.status === "on_track" ? "On track" : g.status === "slipping" ? "Slipping" : "Stalled";
                return (
                  <li key={`${i}:${g.label}`} className="bg-[#121212] border border-[#1a1a1a] rounded-md p-4" data-testid={`sample-goal-${i}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <span className="text-[#fafafa] text-sm">{g.label}</span>
                      <span className="text-[10px] tracking-[2px] uppercase font-medium shrink-0" style={{ color }}>
                        {label}
                      </span>
                    </div>
                    <div className="w-full bg-[#0a0a0a] rounded-full h-1.5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${g.progress}%`, backgroundColor: color }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <Section eyebrow="Challenges" title="What's hard right now" items={SAMPLE.challenges} empty="—" />

          {/* Resolutions / action items */}
          <section>
            <p className="label-eyebrow">Resolutions</p>
            <h3 className="font-serif text-2xl mt-2">Signed off & carried forward</h3>
            <ul className="mt-4 space-y-2">
              {SAMPLE.action_items.map((a, i) => (
                <li
                  key={`${i}:${a.text}`}
                  className="flex items-start gap-3 bg-[#121212] border border-[#1a1a1a] rounded-md p-3"
                  data-testid={`sample-action-${i}`}
                >
                  {a.signed ? (
                    <CheckCircle2 className="w-5 h-5 text-[#c89f45] shrink-0 mt-0.5" strokeWidth={1.5} />
                  ) : (
                    <Circle className="w-5 h-5 text-[#444] shrink-0 mt-0.5" strokeWidth={1.5} />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm ${a.signed ? "text-[#fafafa]" : "text-[#aaa]"}`}>{a.text}</p>
                    <p className="text-xs text-[#666] mt-0.5">
                      {a.signed ? "Signed" : "Open"} · due {a.due}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="gold-divider" />

        {/* Bottom CTA */}
        <div className="my-10 bg-[#121212] border border-[#c89f45]/40 rounded-lg p-6 sm:p-8 text-center">
          <p className="label-eyebrow">Your turn</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#fafafa] mt-2 mb-3">
            Ready to write your own board&apos;s minutes?
          </h2>
          <p className="text-sm text-[#aaa] max-w-md mx-auto mb-6">
            Free forever for solo sessions. AI-drafted minutes on paid plans. Founding Members lock in
            lifetime access for one payment.
          </p>
          <Link to="/meetings/new" data-testid="sample-cta-bottom">
            <Button className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-11 px-5 font-medium">
              Call your first session
              <ArrowUpRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
